import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import MikaCard from '../../components/MikaCard/MikaCard';
import moment from 'moment';

interface Props {
    data: any[];
    onActivate: (row: any) => void;
}

const getInitials = (name: string): string => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return (parts[0]?.charAt(0) + (parts[1]?.charAt(0) || '')).toUpperCase();
};

const getAvatarColor = (name: string): string => {
    const colors = ['#3b82f6', '#8b5cf6', '#06b6d4', '#f59e0b', '#ef4444', '#22c55e', '#ec4899', '#6366f1'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
};

const PassiveProductCardGrid: React.FC<Props> = ({ data, onActivate }) => {
    const intl = useIntl();
    const activateTooltip = intl.formatMessage({ id: 'production.passive.activateAction', defaultMessage: 'Aktivizo' });

    return (
        <div className="ccg-grid">
            {data.length === 0 ? (
                <div className="ccg-empty">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
                        <path d="M2 20h20" /><path d="M5 20V10l7-7 7 7v10" /><path d="M9 20v-6h6v6" />
                    </svg>
                    <p><FormattedMessage id="production.passive.empty" defaultMessage="Nuk u gjet asnjë produkt pasiv" /></p>
                </div>
            ) : (
                data.map((row, idx) => {
                    const initials = getInitials(row.clientName || '');
                    const avatarColor = getAvatarColor(row.clientName || '');

                    return (
                        <MikaCard
                            key={idx}
                            onClick={() => onActivate(row)}
                            header={
                                <div className="ccg-card-header">
                                    <div className="ccg-avatar" style={{ background: avatarColor }}>{initials}</div>
                                    <div className="ccg-card-name">
                                        <span className="ccg-name">{row.productName || '\u2014'}</span>
                                        <span className="ccg-company">{row.clientName}</span>
                                    </div>
                                    <span className="ccg-status-badge" style={{ color: '#94a3b8', background: '#f1f5f9' }}>
                                        <FormattedMessage id="production.passive.badge" defaultMessage="Pasive" />
                                    </span>
                                </div>
                            }
                            body={
                                <div className="ccg-card-body-content">
                                    <div className="ccg-info-grid">
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="production.detail.project" defaultMessage="Projekti" /></span>
                                            <span className="ccg-info-value">{row.projectName || '\u2014'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="production.detail.start" defaultMessage="Fillimi" /></span>
                                            <span className="ccg-info-value">{row.start ? moment(row.start).format('DD-MM-YYYY') : '\u2014'}</span>
                                        </div>
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label"><FormattedMessage id="production.detail.end" defaultMessage="Mbarimi" /></span>
                                            <span className="ccg-info-value">{row.end ? moment(row.end).format('DD-MM-YYYY') : '\u2014'}</span>
                                        </div>
                                        {row.quantity && (
                                            <div className="ccg-info-item">
                                                <span className="ccg-info-label"><FormattedMessage id="production.detail.quantity" defaultMessage="Sasia" /></span>
                                                <span className="ccg-info-value">{row.quantity}{row.measureUnit ? ` ${row.measureUnit}` : ''}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            }
                            footer={
                                <div className="ccg-card-footer">
                                    <span className="ccg-date" style={{ color: row.measureDone ? '#22c55e' : '#f59e0b', fontSize: 11 }}>
                                        {row.measureDone
                                            ? <FormattedMessage id="production.detail.measureFinal" defaultMessage="Matje finale" />
                                            : <FormattedMessage id="production.detail.measureInitial" defaultMessage="Matje fillestare" />}
                                    </span>
                                    <div className="ccg-footer-actions">
                                        <button className="ccg-action-btn" onClick={(e) => { e.stopPropagation(); onActivate(row); }} title={activateTooltip}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polygon points="5 3 19 12 5 21 5 3" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            }
                        />
                    );
                })
            )}
        </div>
    );
};

export default PassiveProductCardGrid;
